import { useState } from 'react';
import { RefreshCw, User, Send, ExternalLink, Link2, Unlink2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useInviteClientTrigger, type Tag } from '@client/features/invite-client';
import {
  AlertDialog,
  AlertDialogTitle,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import Tooltip from '@mui/material/Tooltip';
import type { IntegrationContact } from '@/api/types';

interface ContactRowProps {
  contact: IntegrationContact;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  onViewClient: (clientId: string) => void;
  onLink?: (contact: IntegrationContact) => void;
  onUnlink?: (contact: IntegrationContact) => void;
  isUnlinking: boolean;
  crmName: string;
  getExternalUrl?: (contactId: string) => string;
  hasLinkSupport: boolean;
}

function ContactRow({
  contact,
  isSelected,
  onToggleSelect,
  onViewClient,
  onLink,
  onUnlink,
  isUnlinking,
  crmName,
  getExternalUrl,
  hasLinkSupport,
}: ContactRowProps) {
  const hasEmail = !!contact.email;
  const isBusiness = contact.type?.toLowerCase() === 'business';
  const isDisabled = !hasEmail || isBusiness;

  return (
    <TableRow
      className={`group ${isDisabled ? 'opacity-60' : 'hover:bg-gray-50'}`}
      data-state={isSelected ? 'selected' : undefined}
    >
      <TableCell className="w-14 pl-4">
        <div className="flex h-8 w-8 items-center justify-center">
          {isDisabled ? (
            <User className="h-5 w-5 text-gray-300" />
          ) : isSelected ? (
            <Checkbox
              checked={true}
              onCheckedChange={() => onToggleSelect(contact.id)}
              aria-label={`Select ${contact.firstName} ${contact.lastName}`}
            />
          ) : (
            <>
              <User className="h-5 w-5 text-gray-500 group-hover:hidden" />
              <Checkbox
                checked={false}
                onCheckedChange={() => onToggleSelect(contact.id)}
                className="hidden group-hover:flex"
                aria-label={`Select ${contact.firstName} ${contact.lastName}`}
              />
            </>
          )}
        </div>
      </TableCell>
      <TableCell className="font-medium text-gray-900">
        {getExternalUrl ? (
          <Tooltip
            title={`Open in ${crmName}`}
            placement="top"
          >
            <a
              href={getExternalUrl(contact.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary-hover inline-flex w-fit items-center gap-1 hover:underline"
            >
              {contact.firstName ?? contact.companyName} {contact.lastName}
            </a>
          </Tooltip>
        ) : (
          <span>
            {contact.firstName ?? contact.companyName} {contact.lastName}
          </span>
        )}
      </TableCell>
      <TableCell className="text-gray-700">
        {contact.email || <span className="text-gray-500">No email</span>}
      </TableCell>
      <TableCell className="text-gray-700">{contact.status ?? '—'}</TableCell>
      {hasLinkSupport && (
        <>
          <TableCell className="text-gray-700">
            {contact.waterlilyClientId ? (
              <button
                onClick={() => onViewClient(contact.waterlilyClientId!)}
                className="text-primary hover:text-primary-hover flex items-center gap-1 hover:underline"
              >
                View
                <ExternalLink className="h-3 w-3" />
              </button>
            ) : (
              <span className="text-gray-500">—</span>
            )}
          </TableCell>
          <TableCell className="text-gray-700">
            {!isDisabled &&
              (contact.waterlilyClientId ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onUnlink?.(contact)}
                  disabled={isUnlinking}
                  className="border-red-200 text-red-600 hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                >
                  <Unlink2 className="h-4 w-4" />
                  Unlink
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onLink?.(contact)}
                >
                  <Link2 className="h-4 w-4" />
                  Link
                </Button>
              ))}
          </TableCell>
        </>
      )}
    </TableRow>
  );
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export interface ContactsProps {
  contacts: IntegrationContact[];
  totalPages: number;
  isFetching: boolean;
  error: Error | null;

  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  search: string;
  onSearchChange: (search: string) => void;
  onRefetch: () => void;

  onLink?: (contact: IntegrationContact) => void;
  onUnlink?: (contactId: string) => void;
  isUnlinking?: boolean;

  crmName: string;
  getExternalUrl?: (contactId: string) => string;
}

export function Contacts({
  contacts,
  totalPages,
  isFetching,
  error,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  search,
  onSearchChange,
  onRefetch,
  onLink,
  onUnlink,
  isUnlinking = false,
  crmName,
  getExternalUrl,
}: ContactsProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [contactToUnlink, setContactToUnlink] = useState<IntegrationContact | null>(null);
  const { setInviteClientParams } = useInviteClientTrigger();
  const navigate = useNavigate();

  const hasLinkSupport = !!onLink && !!onUnlink;
  const colSpan = hasLinkSupport ? 6 : 4;

  const handlePageSizeChange = (value: string) => {
    onPageSizeChange(Number(value));
    onPageChange(1);
    setSelectedIds(new Set());
  };

  const handlePageChange = (newPage: number) => {
    onPageChange(newPage);
    setSelectedIds(new Set());
  };

  const handleViewClient = (clientId: string) => {
    navigate(`/clients/${clientId}/onboarding/overview`);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectableContacts = contacts.filter(c => {
    const hasEmail = !!c.email;
    const isBusiness = c.type?.toLowerCase() === 'business';
    return hasEmail && !isBusiness;
  });

  const toggleSelectAll = () => {
    if (selectedIds.size === selectableContacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableContacts.map(c => c.id)));
    }
  };

  const allSelected =
    selectableContacts.length > 0 && selectedIds.size === selectableContacts.length;

  const selectedContacts = contacts.filter(c => selectedIds.has(c.id));
  const unlinkedSelectedContacts = selectedContacts.filter(c => !c.waterlilyClientId);

  const selectedEmailTags: Tag[] = unlinkedSelectedContacts
    .filter(c => !!c.email)
    .map(c => ({
      value: c.email!,
      isValid: true,
      error: '',
      contactId: c.id,
      firstName: c.firstName ?? '',
      lastName: c.lastName ?? '',
    }));

  const canInvite = unlinkedSelectedContacts.length > 0;

  const handleImport = () => {
    const singleContactId =
      unlinkedSelectedContacts.length === 1 ? unlinkedSelectedContacts[0].id : undefined;
    setInviteClientParams('email', selectedEmailTags, singleContactId);
    setSelectedIds(new Set());
  };

  const confirmUnlink = () => {
    if (contactToUnlink && onUnlink) {
      onUnlink(contactToUnlink.id);
      setSelectedIds(new Set());
      setContactToUnlink(null);
    }
  };

  let placeholder;
  if (isFetching) {
    placeholder = (
      <TableRow>
        <TableCell
          colSpan={colSpan}
          className="h-48"
        >
          <div className="flex items-center justify-center">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-500" />
          </div>
        </TableCell>
      </TableRow>
    );
  } else if (error) {
    placeholder = (
      <TableRow>
        <TableCell
          colSpan={colSpan}
          className="h-48"
        >
          <div className="flex items-center justify-center text-red-700">
            {error.message || 'Failed to load contacts'}
          </div>
        </TableCell>
      </TableRow>
    );
  } else if (contacts.length === 0) {
    placeholder = (
      <TableRow>
        <TableCell
          colSpan={colSpan}
          className="h-48"
        >
          <div className="flex items-center justify-center text-gray-700">
            {search ? 'No contacts match your search' : 'No contacts found'}
          </div>
        </TableCell>
      </TableRow>
    );
  } else {
    placeholder = contacts.map(contact => (
      <ContactRow
        key={contact.id}
        contact={contact}
        isSelected={selectedIds.has(contact.id)}
        onToggleSelect={toggleSelect}
        onViewClient={handleViewClient}
        onLink={onLink}
        onUnlink={setContactToUnlink}
        isUnlinking={isUnlinking}
        crmName={crmName}
        getExternalUrl={getExternalUrl}
        hasLinkSupport={hasLinkSupport}
      />
    ));
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="relative w-80">
              <Input
                placeholder="Search contacts..."
                value={search}
                onChange={e => onSearchChange(e.target.value)}
                className="pr-8"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => onSearchChange('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {selectedIds.size > 0 && (
              <div className="text-sm font-medium text-gray-900">
                {selectedIds.size} {selectedIds.size === 1 ? 'Contact' : 'Contacts'} selected
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => onRefetch()}
              disabled={isFetching}
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={handleImport}
              disabled={!canInvite}
            >
              <Send className="h-4 w-4" />
              Invite
            </Button>
          </div>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-14 pl-4">
                  <div className="flex h-8 w-8 items-center justify-center">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all"
                      disabled={isFetching || !!error}
                    />
                  </div>
                </TableHead>
                <TableHead className="text-gray-900">Name</TableHead>
                <TableHead className="text-gray-900">Email</TableHead>
                <TableHead className="text-gray-900">Status</TableHead>
                {hasLinkSupport && (
                  <>
                    <TableHead className="text-gray-900">Waterlily</TableHead>
                    <TableHead className="text-gray-900">Actions</TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>{placeholder}</TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Rows per page</span>
            <Select
              value={String(pageSize)}
              onValueChange={handlePageSizeChange}
            >
              <SelectTrigger className="w-[80px] text-gray-900">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map(size => (
                  <SelectItem
                    key={size}
                    value={String(size)}
                  >
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1 || isFetching}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-700">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages || isFetching}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {hasLinkSupport && (
        <AlertDialog
          open={!!contactToUnlink}
          onOpenChange={open => !open && setContactToUnlink(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Unlink Contact</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to unlink{' '}
                <span className="font-medium text-gray-900">
                  {contactToUnlink?.firstName ?? contactToUnlink?.companyName}{' '}
                  {contactToUnlink?.lastName}
                </span>{' '}
                from their Waterlily client? This would also remove waterlily's user defined fields
                from {crmName}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmUnlink}
                className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              >
                {isUnlinking ? 'Unlinking...' : 'Unlink'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
